import type { DashboardFase } from '@/api/projectFases';
import { faseStatus, type FaseAggregate } from '@/utils/dashboardFases';
import { useSystemDot } from '@/stores/systems';

interface FaseStatusStripProps {
  fase: DashboardFase;
  aggregate: FaseAggregate;
  dateRange: { from: string; to: string } | null;
  /** Rendered inside a DashboardSection coloured shell (top of the "Planning"
   *  section) — drop our own outer card chrome. The fase-name / date-range /
   *  status header + the systemMix m²-per-systeem strip stay (genuine content,
   *  NOT a duplicate of the shell's "Planning" title). */
  embedded?: boolean;
}


// Solid status chips with navy text — clearly differentiated AND WCAG-AA readable
// (light tints + same-hue text failed AA at 12px bold).
const STATUS: Record<string, { label: string; cls: string }> = {
  gepland: { label: 'Gepland', cls: 'bg-gray-200 text-td-navy' },
  in_uitvoering: { label: 'In uitvoering', cls: 'bg-td-blue text-td-navy' },
  uitgevoerd: { label: 'Uitgevoerd', cls: 'bg-td-green text-td-navy' },
};

/** "yyyy-mm-dd" → "dd/mm"; collapses a single-day range. */
function fmtRange(range: { from: string; to: string } | null): string | null {
  if (!range) return null;
  const d = (s: string) => `${s.slice(8, 10)}/${s.slice(5, 7)}`;
  return range.from === range.to ? d(range.from) : `${d(range.from)} – ${d(range.to)}`;
}

/** Per-fase status strip: systems, total m², collector progress, derived status. */
export function FaseStatusStrip({ fase, aggregate, dateRange, embedded = false }: FaseStatusStripProps) {
  const badge = STATUS[faseStatus(aggregate)];
  const range = fmtRange(dateRange);
  // Colour dots come from the Odoo-defined system roster.
  const systemDot = useSystemDot();

  const Wrapper = embedded ? 'div' : 'section';
  return (
    <Wrapper
      aria-label={fase.name}
      className={embedded ? '' : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm'}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-base font-extrabold text-td-navy truncate" title={fase.name}>{fase.name}</h3>
          {range && <span className="text-xs font-semibold text-td-muted shrink-0">{range}</span>}
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold ${badge.cls}`}>{badge.label}</span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
        {aggregate.systemMix.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {aggregate.systemMix.map((s) => (
              <span key={s.systeem} className="inline-flex items-center gap-1.5 text-sm font-bold text-td-navy">
                <span className={`h-2 w-2 rounded-full ${systemDot(s.systeem)}`} aria-hidden="true" />
                {s.systeem}
                <span className="font-normal text-td-muted">· {Math.round(s.m2)} m²</span>
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm text-td-muted">Geen collectoren in deze fase</span>
        )}

        <span className="ml-auto text-sm font-bold text-td-navy">
          {`${Math.round(aggregate.totalM2)} m²`}
          <span className="ml-2 font-normal text-td-muted">
            {`${aggregate.uitgevoerd} / ${aggregate.count} collectoren uitgevoerd`}
          </span>
        </span>
      </div>
    </Wrapper>
  );
}
